import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_LOOKUP,
  DELETE_LOOKUP,
  GET_ALL_LOOKUP,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  LOOKUP_CATEGORY_LABEL,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { LOOKUP_CATEGORY } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastSuccess,
} from "../../utils/AppFunctions";

export interface ILookup {
  id: string;
  display_value: string;
  data_value: string;
  lookup_category: string;
  sort_order: number | null;
  is_active: TBitValue;
}

export function getLookupFields<T>(
  actionComponent: (item: T) => JSX.Element,
  handleLookupStatus: (item: T) => void,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("display_value"),
      key: "display_value",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-3",
    },
    {
      name: t("data_value"),
      key: "data_value",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("lookup_category"),
      key: "lookup_category",
      sort: true,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-3",
      format: ({ field, data }: ITabelFieldViewProps<T>) => {
        if (Object.values(LOOKUP_CATEGORY).includes(data["lookup_category"])) {
          return t(
            LOOKUP_CATEGORY_LABEL[
              data["lookup_category"] as keyof typeof LOOKUP_CATEGORY_LABEL
            ]
          );
        }
        return "-";
      },
    },
    {
      name: t("sort_order"),
      key: "sort_order",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("status"),
      key: "is_active",
      sort: false,
      type: TABLE_FIELD_TYPE.switch,
      align: TABLE_FIELD_ALIGN.center,
      checkedValue: "1",
      flex: "col-span-1",
      handleCheck: handleLookupStatus,
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllLookups = async (
  setLookup: TReactSetState<ILookup[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: string
) => {
  try {
    const lookups = await GET_ALL_LOOKUP(pagination, search, advanceSearch);
    if (lookups && lookups.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setLookup(lookups.data.item);
      setPagination(lookups.data.pagination);
    } else {
      setLookup([]);
      sweetAlertError({
        message: lookups.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllLookups");
  }
};

export const deleteLookup = async (id: string) => {
  try {
    const deleteData = await DELETE_LOOKUP(id);
    if (deleteData && deleteData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deleteData.message });
      return true;
    } else {
      sweetAlertError({
        message: deleteData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteLookup");
    return false;
  }
};

export const activeInactiveLookup = async (id: string, values: TBitValue) => {
  try {
    const activeInactiveData = await ACTIVE_INACTIVE_LOOKUP(id, {
      is_active: values,
    });
    if (
      activeInactiveData &&
      activeInactiveData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      toastSuccess(activeInactiveData.message);
      return true;
    } else {
      sweetAlertError({
        message: activeInactiveData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "activeInactiveLookup");
    return false;
  }
};
